import { ACCOUNT_DASHBOARD_ENABLED, CLERK_ENABLED, CLERK_PUBLISHABLE_KEY } from './lib/environment';
import React, { lazy } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/ff-layout.css';
import './styles/ff-header.css';
import './styles/ff-footer.css';
import './styles/ff-swiss.css';
import './styles/ff-responsive.css';
import './styles/ff-skyops.css';
import './styles/ff-deanair.css';
import './styles/ff-account.css';
import './styles/ff-system.css';
import Home from './pages/Home';
import Layout from './pages/Layout';
import NotFound from './pages/NotFound';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { startEarlyData } from './lib/earlyData';

const PostList = lazy(() => import('./pages/PostList'));
const PostPage = lazy(() => import('./pages/PostPage'));
const Tools = lazy(() => import('./pages/Tools'));
const Contact = lazy(() => import('./pages/Contact'));
const QFWineTracker = lazy(() => import('./pages/QFWineTracker'));
const QFMarketplaceTracker = lazy(() => import('./pages/QFMarketplaceTracker'));
const ClassicRewardsStatusCreditCalculator = lazy(() => import('./pages/ClassicRewardsStatusCreditCalculator'));
const SetupAccount = lazy(() => import('./pages/SetupAccount'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const SignInPage = lazy(() => import('./components/SignInPage').then((m) => ({ default: m.SignInPage })));
const AccountPage = lazy(() => import('./components/AccountPage').then((m) => ({ default: m.AccountPage })));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const Games = lazy(() => import('./pages/Games'));
const SkyOps = lazy(() => import('./pages/SkyOps'));
const WhatsThePoint = lazy(() => import('./pages/WhatsThePoint'));
const WhatsThePointV2 = lazy(() => import('./pages/WhatsThePointV2'));
const IataDash = lazy(() => import('./pages/IataDash'));
const DeanAir = lazy(() => import('./pages/DeanAir'));
const Cap = lazy(() => import('./pages/shop/Cap'));

if (!CLERK_ENABLED) {
  console.warn('Clerk disabled: set REACT_APP_CLERK_PUBLISHABLE_KEY to enable auth.');
}

function AppRoutes() {
  const protectedElement = CLERK_ENABLED ? <SetupAccount /> : <Navigate to="/" replace />;
  const signInElement = CLERK_ENABLED ? <SignInPage /> : <Navigate to="/" replace />;
  const accountElement = CLERK_ENABLED ? <AccountPage /> : <Navigate to="/" replace />;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="posts" element={<PostList />} />
          <Route path="tools" element={<Tools />} />
          <Route path="contact" element={<Contact />} />
          <Route path="tools/qfwine" element={<QFWineTracker />} />
          <Route path="tools/qfflightfinder" element={<Navigate to="/tools/qfwine" replace />} />
          <Route path="tools/qfmarketplace" element={<QFMarketplaceTracker />} />
          <Route path="tools/reward-sc-calculator" element={<ClassicRewardsStatusCreditCalculator />} />
          <Route path="d/:source/:key" element={<Layout.DealLinkRedirect />} />
          <Route path="games" element={<Games />} />
          <Route path="games/deanair" element={<DeanAir />} />
          <Route path="games/skyops" element={<SkyOps />} />
          <Route path="games/whats-the-point" element={<WhatsThePoint />} />
          <Route path="games/whats-the-point-v2" element={<WhatsThePointV2 />} />
          <Route path="games/iata-dash" element={<IataDash />} />
          <Route path="/posts/:postId" element={<PostPage />} />
          <Route path="/tos" element={<TermsOfService />} />
          <Route path="/pp" element={<PrivacyPolicy />} />
          <Route path="signup" element={protectedElement} />
          <Route path="setup-account" element={protectedElement} />
          <Route path="signin" element={signInElement} />
          <Route path={ACCOUNT_DASHBOARD_ENABLED ? 'account/*' : 'account'} element={accountElement} />
          <Route path="shop" element={<Navigate to="/cap" replace />} />
          <Route path="shop/:slug" element={<Navigate to="/cap" replace />} />
          <Route path="cap" element={<Cap />} />
          <Route path="nocap" element={<Navigate to="/cap" replace />} />
          <Route path="cap1" element={<Navigate to="/cap" replace />} />
          <Route path="cap2" element={<Navigate to="/cap" replace />} />
          <Route path="cap3" element={<Navigate to="/cap" replace />} />
          <Route path="cap4" element={<Navigate to="/cap" replace />} />
          <Route path="cap5" element={<Navigate to="/cap" replace />} />
          <Route path="cap/confirmation" element={<OrderConfirmation />} />
          <Route path="shop/hat" element={<Navigate to="/cap" replace />} />
          <Route path="shop/order-confirmation" element={<Navigate to="/cap/confirmation" replace />} />
          <Route path="hat" element={<Navigate to="/cap" replace />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  if (!CLERK_ENABLED) return <AppRoutes />;

  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} signUpForceRedirectUrl={"/setup-account"} signInForceRedirectUrl="/tools/qfwine">
      <AppRoutes />
    </ClerkProvider>
  );
}

// Start this page's first data request now, alongside the page's own code.
startEarlyData();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
